import {
  TableColumn,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
} from '@aibees/shared/components';
import type { TableProps as SharedTableProps } from '@aibees/shared/components/table';
import TableSkeletonList from '@aibees/shared/components/table/table-skeleton-list';
import { LOG_API_CALL_DURATION_ENABLE } from '@aibees/shared/constants/app';
import { useTableLayoutTemplateContext } from '@aibees/shared/features/advance-table/components/table-layout-modifier-dialog/context';
import { useScrollDirection } from '@aibees/shared/hooks';
import { useProgressBarStore } from '@aibees/shared/store/progress-bar';
import { classnames } from '@aibees/shared/utils';
import { Box, TableBody, TableContainer } from '@mui/material';
import type { Table as ReactTable } from '@tanstack/react-table';
import { get } from 'lodash-es';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useAdvanceTableConfigContext,
  useAdvanceTableDataBrowseContext,
  useAdvanceTableDataFiltersContext,
  useAdvanceTableStickyContext,
  useAdvanceTableTableContext,
} from '../../context';
import { Collapse } from '../collapse';
import { TableLayoutModifier } from '../table-layout-modifier';
import { TableRow } from '../table-row';
import { TableSequentialPagination } from '../table-sequential-pagination';
import { TableStickyRowsWrapper } from '../table-sticky-rows-wrapper';
import { useGetBrowseIdsApiData } from './hooks';
import { useApplyTableFiltersToQueryParams } from './hooks/use-apply-table-filters-to-query-params';
import { useFilterChangeListener } from './hooks/use-filter-change-listener';
import { useProgressBarState } from './hooks/use-progress-bar-state';
import { useTableHeight } from './hooks/use-table-height';
import * as Styled from './styles';
import * as SX from './sx';
import type { TableProps as Props } from './types';
import { TableOnboarding } from '../table-onboarding';

/** Shallow compare for table body rows props so we only re-render when rows-relevant props change. */
function tableBodyRowsPropsAreEqual(
  prev: {
    reactTable: ReactTable<any>;
    rowKey: string;
    table: any;
    columns: any[];
    config: any;
    checkboxEnabled: boolean;
    onRowClick: (item: any, key: any) => void;
  },
  next: typeof prev
): boolean {
  return (
    prev.reactTable === next.reactTable &&
    prev.rowKey === next.rowKey &&
    prev.table === next.table &&
    prev.columns === next.columns &&
    prev.config === next.config &&
    prev.checkboxEnabled === next.checkboxEnabled &&
    prev.onRowClick === next.onRowClick
  );
}

const INITIAL_VISIBLE_ROWS = 12;

/** Computed once per TableBodyRows instance via ref; based on viewport height at mount. */
function getInitialVisibleRows(): number {
  if (typeof window === 'undefined') return INITIAL_VISIBLE_ROWS;
  const height = window.innerHeight;
  if (height <= 720) return 7;
  if (height <= 900) return 10;
  if (height <= 1080) return 13;
  if (height <= 1440) return 20;
  return 25;
}

/**
 * Renders table body rows using TanStack's memoized row model.
 * Keeps progressive rendering: shows an initial subset of rows, then
 * expands to the full set on the next animation frame.
 * Memoized with custom areEqual so we only re-render when rows-relevant props change,
 * not on every Table re-render from context updates.
 */
const TableBodyRows = memo(function TableBodyRows({
  reactTable,
  rowKey,
  table,
  columns,
  config,
  checkboxEnabled,
  onRowClick,
}: {
  reactTable: ReactTable<any>;
  rowKey: string;
  table: any;
  columns: any[];
  config: any;
  checkboxEnabled: boolean;
  onRowClick: (item: any, key: any) => void;
}) {
  // TanStack's getRowModel().rows is internally memoized: row objects are
  // stable across re-renders unless data or table state actually changes.
  const { rows } = reactTable.getRowModel();

  // Progressive rendering: show a viewport-sized subset first, expand via rAF
  const initialVisibleRowsRef = useRef<number | null>(null);
  if (initialVisibleRowsRef.current === null) {
    initialVisibleRowsRef.current = getInitialVisibleRows();
  }
  const initialVisibleRows = initialVisibleRowsRef.current;

  const [visibleCount, setVisibleCount] = useState(initialVisibleRows);
  const visibleRows =
    rows.length <= initialVisibleRows ? rows : rows.slice(0, visibleCount);

  useEffect(() => {
    if (rows.length <= initialVisibleRows) return;
    const id = requestAnimationFrame(() => setVisibleCount(rows.length));
    return () => cancelAnimationFrame(id);
  }, [rows.length, initialVisibleRows]);

  return (
    <>
      {visibleRows.map((row, i) => {
        const item = row.original;
        const key = get(item, rowKey);
        const index = table.page * table.rowsPerPage + i;
        return (
          <TableRow
            key={key}
            data-key={key}
            item={item}
            index={index}
            columns={columns}
            config={config}
            selected={table.selection.isRowSelected(index, key)}
            checkboxEnabled={checkboxEnabled}
            onSelect={table.selection.onSelectRow}
            onRowClick={onRowClick}
          />
        );
      })}
    </>
  );
},
tableBodyRowsPropsAreEqual);

/**
 * Renders legacy table prepend (renderTablePrepend) in a component that
 * subscribes to DataFiltersContext so the main Table does not re-render
 * when only filters change.
 */
const LegacyTablePrepend = memo(function LegacyTablePrepend({
  columnModifier,
}: {
  columnModifier: React.ReactNode;
}) {
  const { config } = useAdvanceTableConfigContext();
  const { table } = useAdvanceTableTableContext();
  const { filters } = useAdvanceTableDataFiltersContext();
  const { browseData } = useAdvanceTableDataBrowseContext();
  const data = browseData?.data;
  if (!config.renderTablePrepend && !columnModifier) return null;
  return config.renderTablePrepend
    ? (config.renderTablePrepend(columnModifier, {
        data,
        table,
        filters,
      }) as React.ReactElement)
    : (columnModifier as React.ReactElement);
});

/**
 * Hosts useApplyTableFiltersToQueryParams & useFilterChangeListener in a
 * separate fiber so their useSearchParamsSource() subscription does not
 * trigger re-renders of the heavy Table component.
 */
function ParamsSyncController({ table }: { table: SharedTableProps }) {
  useApplyTableFiltersToQueryParams(table);
  useFilterChangeListener(table);
  return null;
}

/**
 * Manages progress-bar timer + Zustand store sync in its own fiber so that
 * frequent timer-driven state changes do not cause the heavy Table to re-render.
 */
function ProgressBarController({ isLoading }: { isLoading: boolean }) {
  const progressBar = useProgressBarState(isLoading);
  const setEnable = useProgressBarStore((s) => s.setEnable);
  const setValue = useProgressBarStore((s) => s.setValue);

  useEffect(() => {
    setEnable(progressBar.progressBarVisible);
    setValue(progressBar.loadingProgress);
  }, [progressBar, setEnable, setValue]);

  return null;
}

export const Table = memo(({ className }: Props) => {
  const { config } = useAdvanceTableConfigContext();
  const { table } = useAdvanceTableTableContext();
  const { filters } = useAdvanceTableDataFiltersContext();
  const { browseData, reactTable } = useAdvanceTableDataBrowseContext();
  const { setStickyCollapsed } = useAdvanceTableStickyContext();
  const { config: modifiedConfig } = useTableLayoutTemplateContext();

  const height = useTableHeight();
  const rowKey = config.rowKey || 'id';

  // Memoize to prevent re-calculation on every render
  const checkboxEnabled = useMemo(
    () =>
      config.manualSelectionLimit
        ? table.selection.added.length < config.manualSelectionLimit
        : true,
    [config.manualSelectionLimit, table.selection.added.length]
  );

  const hidePagination = !!config.hidePagination;

  const {
    data,
    isLoading,
    handleNextPage,
    handlePreviousPage,
    enabledNextButton,
    enabledPrevButton,
  } = browseData;
  const browseIdsApi = useGetBrowseIdsApiData(table);
  const { notFound, showOnboarding, showNoData } = useMemo(() => {
    const searchKey = config.searchFilterKey ?? 'name';
    const searchFilterValue = filters?.[searchKey];
    const isSearchEmpty =
      !searchFilterValue || String(searchFilterValue).trim() === '';
    const isEmpty = !isLoading && !data?.content?.length && !data?.top?.length;
    const shouldOnboard = isEmpty && isSearchEmpty && !!config.onboardingProps;
    return {
      notFound: isEmpty,
      showOnboarding: shouldOnboard,
      showNoData: isEmpty && !shouldOnboard,
    };
  }, [
    isLoading,
    data?.content?.length,
    data?.top?.length,
    filters,
    config.searchFilterKey,
    config.onboardingProps,
  ]);

  const ref = useRef<any>(null);
  const { scrollDirection } = useScrollDirection(ref);

  const columns = useMemo(() => {
    const source = modifiedConfig?.columns ?? config.columns;
    return source.filter((x) => {
      // if (
      //   (config?.columnModifierKey &&
      //     config.columnModifierPresetType &&
      //     selectedPreset?.value === 'default') ||
      //   !!templates?.length
      // ) {
      //   return !x.hide;
      // }
      return !x.hide;
    });
  }, [modifiedConfig?.columns, config.columns]);

  useEffect(() => {
    // API→table and filter→table KPI logs removed per new requirements.
    // This effect is retained for possible future extensions.
    if (!data || isLoading) return;
    if (!LOG_API_CALL_DURATION_ENABLE) return;
  }, [data, isLoading, config.apiResponsePerformanceMark]);

  useEffect(() => {
    if (browseIdsApi?.isError) table.selection.onClear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseIdsApi?.isError]);

  useEffect(() => {
    if (scrollDirection === 'down') setStickyCollapsed(true);
  }, [scrollDirection, setStickyCollapsed]);

  useEffect(() => {
    ref.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [data?.number]);

  // Sync selection state when data changes: append initial IDs from sticky rows
  // and refresh selected items with fresh data objects — in a single effect so
  // only one state update (and thus one context change / re-render) occurs.
  useEffect(() => {
    const ids = data?.top?.map((x) => get(x, rowKey)) || [];
    table.selection.appendInitialIds(ids);

    const { items, setItems } = table.selection;
    if (!data?.content || !items.length) return;

    const updatedSelectedItems = items.map((item) => {
      const foundItem = data.content.find((i) => i.id === item.id);
      return foundItem || item;
    });

    const hasChanges = updatedSelectedItems.some(
      (item, idx) => item !== items[idx]
    );

    if (hasChanges) {
      setItems(updatedSelectedItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.top, data?.content, rowKey]);

  // Memoized handlers
  const handleRowClick = useCallback(
    (item: any, key: any) => config.onClickRow?.(item, key),
    [config]
  );

  const onPageChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement> | null, page: number) =>
      table.onChangePage?.(event, page),
    [table]
  );
  const onRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      table.onChangeRowsPerPage?.(event),
    [table]
  );

  const tablePaginationCustomProps = useMemo(
    () => ({
      isLoading,
      config,
      count: data?.totalElements || 0,
      page: table.page,
      rowsPerPage: table.rowsPerPage,
      onPageChange,
      onRowsPerPageChange,
    }),
    [
      isLoading,
      config,
      data?.totalElements,
      table.page,
      table.rowsPerPage,
      onPageChange,
      onRowsPerPageChange,
    ]
  );

  const columnModifier = useMemo(
    () =>
      config?.columnModifierKey && config.columnModifierPresetType ? (
        <TableLayoutModifier />
      ) : null,
    [config?.columnModifierKey, config.columnModifierPresetType]
  );

  const prependElementStable = useMemo(() => {
    if (!config.renderTablePrependStable) return null;
    return config.renderTablePrependStable(columnModifier);
    // Intentionally depend only on renderTablePrependStable and columnModifier so prepend identity is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.renderTablePrependStable, columnModifier]);

  // Legacy prepend lives in a component that subscribes to DataFiltersContext so
  // Table does not re-render when only filters change.
  const prependElement = config.renderTablePrependStable ? (
    prependElementStable
  ) : (
    <LegacyTablePrepend columnModifier={columnModifier} />
  );

  return (
    <>
      <ParamsSyncController table={table} />
      <ProgressBarController
        isLoading={!!(isLoading || browseIdsApi?.isFetching)}
      />

      {isLoading &&
        config.loadingComponent &&
        React.cloneElement(config.loadingComponent)}

      {!isLoading &&
        (columnModifier || config.renderTablePrepend) &&
        notFound &&
        config.showTablePrependOnNotFound}

      {!notFound && prependElement && <Box>{prependElement}</Box>}

      <Styled.Wrapper
        className={classnames('Table', className, {
          bordered: !notFound && !!config.bordered,
          notFound,
          isLoading,
          hideHeader: config.hideColumnsOnNoData,
        })}
        data-border-style={config.bordered}
      >
        <Collapse />
        <TableContainer
          ref={ref}
          sx={{
            height,
            ...SX.tableContainer,
          }}
        >
          <Styled.Table stickyHeader={config.stickyHeader}>
            {(config.hideColumnsOnNoData ? !notFound : true) && (
              <TableHeadCustom
                order={table.order}
                orderBy={table.orderBy}
                headLabel={columns as TableColumn[]}
                cellSx={config.cellSx}
                onSort={table.onSort}
                canSelectAllRows={
                  !config.rowSelectable || config.showSelectAll
                    ? config.rowSelectable
                    : false
                }
              />
            )}

            <TableBody>
              {(isLoading || browseIdsApi?.isFetching) && (
                <TableSkeletonList
                  count={table.rowsPerPage}
                  columns={columns}
                />
              )}
              <TableStickyRowsWrapper />
              {!browseIdsApi?.isFetching && data?.content && (
                <TableBodyRows
                  key={`page-${table.page}`}
                  reactTable={reactTable}
                  rowKey={rowKey}
                  table={table}
                  columns={columns}
                  config={config}
                  checkboxEnabled={checkboxEnabled}
                  onRowClick={handleRowClick}
                />
              )}
              {showNoData && (
                <TableNoData
                  config={config.emptyProps}
                  isError={!Array.isArray(data?.content)}
                />
              )}
              {showOnboarding && (
                <TableOnboarding onboardingProps={config.onboardingProps} />
              )}
            </TableBody>
          </Styled.Table>
        </TableContainer>
      </Styled.Wrapper>

      {!notFound &&
        !hidePagination &&
        (config.sequentialPagination ? (
          <TableSequentialPagination
            handleNextPage={handleNextPage}
            handlePreviousPage={handlePreviousPage}
            enabledNextButton={enabledNextButton}
            enabledPrevButton={enabledPrevButton}
          />
        ) : (
          <TablePaginationCustom {...tablePaginationCustomProps} />
        ))}
    </>
  );
}) as React.MemoExoticComponent<React.FC<Props>> & {
  whyDidYouRender?: boolean;
};

Table.whyDidYouRender = true;

Table.displayName = 'Table';
